<template>
  <div class="hr-super-api">
    <div v-if="stepper.steps.length > 0" class="__stepper">
      <div class="__stepper-progress-bar">
        <div
          class="__stepper-progress-bar-fill"
          :style="{ '--percentage': `${currentStepPercentage}%` }"
        />
      </div>

      <div class="__stepper-progress-info">
        {{ t('hr-super-api:stepper.step') }} {{ currentStepIndex }}
        {{ t('hr-super-api:stepper.of') }} {{ stepper.steps.length }}
      </div>
    </div>

    <div v-if="isLoading || embedIsLoading" class="hr-super-api-loading">
      <hr-loader-block text="Loading details" />
    </div>

    <div v-else-if="hasFailed" class="hr-super-api-loading-failed">
      <div class="__icon">
        <co-icon name="exclamation-circle" width="24" height="24"></co-icon>
      </div>
      <p class="__title">Unable to load this page</p>
      <p class="__description">
        Something went wrong while displaying this content.
      </p>
      <co-button
        data-testid="hr-super-api-retry"
        size="large"
        @click="reload"
      >Try again</co-button>
    </div>

    <div
      v-show="!isLoading && !hasFailed"
      id="super-api-embed"
      class="hr-super-api"
    />
  </div>
</template>

<script>
import store from '@/store';
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { Embed, MESSAGE_KIND } from '@super-api/super-api-embed';
import { logWebHrError, logWebHrWarning } from '@/core/logger/log-wrapper';
import { CoIcon, CoButton } from '@deputy/copilot2';
import HrLoaderBlock from '@/components/hr-loader-block.vue';
import router from '@/router';
import { DOCUMENT_NAMES } from '@/core/field-names';
import svc from '@/core/svc-client';
import { HR_ONBOARDING_STATES } from '@/core/onboarding-states';
import t from '@/lib/i18next';
import { useAnalytics } from '@/composables/use-analytics';
import { EVENT_NAMES, EVENT_SCREENS } from '@/definitions';

const LOG_SOURCE = 'hr-super-api';

export default {
  name: 'hr-super-api',
  components: {
    CoIcon,
    CoButton,
    HrLoaderBlock,
  },
  setup() {
    const embedIsLoading = ref(false);
    const embedInstance = ref(null);
    let embedGeneration = 0;
    const embedUrlFetchedAt = ref(null);
    const attemptStartedAt = ref(null);
    const { track } = useAnalytics({
      installationId: store.state.onboarding?.company?.id,
      screen: EVENT_SCREENS.SUPER_STEP,
    });
    const superApiEmbed = computed(() => store.state.superApiEmbed);
    const isLoading = computed(() => store.state.loadingSuperApi);
    const hasFailed = computed(() => store.state.loadingSuperApiFailed);
    const needsSessionReset = computed(
      () => store.state.needsSuperStepSessionReset
    );
    const sessionId = computed(
      () => store.state.onboarding.superStepOnboardingSessionId
    );
    const remoteId = computed(
      () => store.state.superApiOnboardingSession?.remote_id
    );

    const LOGGER_MESSAGES = {
      EMBED_ERROR: 'Super api embed error',
      EMBED_WARNING: 'Super api embed warning',
      INIT_ERROR: 'Error initializing super api embed',
    };

    const stepper = reactive({
      steps: [],
      currentStepKey: '',
    });

    const currentStepIndex = computed(() => {
      return (
        stepper.steps.findIndex(
          (stepName) => stepName === stepper.currentStepKey
        ) + 1
      );
    });

    const currentStepPercentage = computed(() => {
      return Math.round((currentStepIndex.value / stepper.steps.length) * 100);
    });

    function setLoading(value) {
      store.commit('setLoadingSuperApi', value);
    }

    function setFailed(value) {
      store.commit('setLoadingSuperApiFailed', value);
      if (value) {
        track(EVENT_NAMES.ONBOARDING_TAX_AND_SUPER_EMBED_LOAD_FAILED);
      }
    }

    function logError(message, metadata = {}, error = null) {
      logWebHrError({
        message,
        source: LOG_SOURCE,
        metadata: {
          ...metadata,
          sessionId: sessionId.value,
          remoteId: remoteId.value,
        },
        error: error || message,
      });
    }

    function logWarning(message, metadata = {}) {
      logWebHrWarning({
        message,
        source: LOG_SOURCE,
        metadata: {
          ...metadata,
          sessionId: sessionId.value,
          remoteId: remoteId.value,
        },
      });
    }

    function teardownEmbed() {
      if (embedInstance.value) {
        embedInstance.value.teardown();
        embedInstance.value = null;
      }
      attemptStartedAt.value = null;
      embedUrlFetchedAt.value = null;
    }

    function getDiagnosticMetadata() {
      const iframeCount = document.querySelectorAll(
        '#super-api-embed iframe'
      ).length;
      const urlAge = embedUrlFetchedAt.value
        ? Math.round((Date.now() - embedUrlFetchedAt.value) / 1000)
        : null;
      const attemptAge = attemptStartedAt.value
        ? Math.round((Date.now() - attemptStartedAt.value) / 1000)
        : null;

      return {
        iframeCount,
        urlAge,
        attemptAge,
        hasUrl: !!superApiEmbed.value.url,
        expiresAt: superApiEmbed.value.expiresAt,
        isExpired: store.getters.isSuperapiEmbedUrlExpired,
        generation: embedGeneration,
      };
    }

    function handleEmbedLoaded() {
      embedIsLoading.value = false;
      track(EVENT_NAMES.ONBOARDING_TAX_AND_SUPER_EMBED_LOADED);
    }

    function handleEmbedLoadError() {
      const diagnostics = getDiagnosticMetadata();

      embedIsLoading.value = false;

      setFailed(true);

      store.commit('setSuperapiEmbed', {
        url: '',
        expiresAt: null,
      });

      logError(
        LOGGER_MESSAGES.EMBED_ERROR,
        { ...diagnostics, trigger: 'onLoadError' },
        new Error('Super api embed failed to load (3s postMessage timeout)')
      );
    }

    function handleSessionFinished() {
      store.dispatch('setSuperApiEmbedCompleted');
      goToNextStep();

      track(EVENT_NAMES.ONBOARDING_TAX_AND_SUPER_FINISHED);
    }

    function handleStepChanged(event) {
      stepper.steps = event.steps;
      stepper.currentStepKey = event.current_step;
    }

    function handleToast(event) {
      embedIsLoading.value = false;

      if (event.kind === 'error') {
        setFailed(true);
        logError(LOGGER_MESSAGES.EMBED_ERROR, {
          ...getDiagnosticMetadata(),
          trigger: 'toast',
          toastMessage: event.message,
        });
      } else if (event.kind === 'warning') {
        logWarning(LOGGER_MESSAGES.EMBED_WARNING, {
          message: event.message,
          event,
        });
      }
    }

    async function fetchEmbedUrlIfNeeded() {
      const needsUrl =
        !superApiEmbed.value.url || store.getters.isSuperapiEmbedUrlExpired;

      if (needsUrl) {
        await store.dispatch('getSuperEmbedUrl');

        if (superApiEmbed.value.url) {
          embedUrlFetchedAt.value = Date.now();
        }
      }
    }

    function createAndMountEmbed() {
      teardownEmbed();

      const element = document.getElementById('super-api-embed');
      const currentGeneration = ++embedGeneration;
      attemptStartedAt.value = Date.now();

      const embed = new Embed({
        element,
        url: superApiEmbed.value.url,
        loaderClass: 'hr-super-api-iframe-loader',
        onLoadError: () => {
          if (currentGeneration !== embedGeneration) {
            return;
          }
          handleEmbedLoadError();
        },
      });

      embed.on(MESSAGE_KIND.LOADED, handleEmbedLoaded);
      embed.on(MESSAGE_KIND.ONBOARDING_SESSION_FINISHED, handleSessionFinished);
      embed.on(MESSAGE_KIND.ONBOARDING_STEP_CHANGED, handleStepChanged);
      embed.on(MESSAGE_KIND.TOAST, handleToast);

      embedInstance.value = embed;
    }

    async function setupEmbed() {
      await fetchEmbedUrlIfNeeded();

      if (hasFailed.value) {
        return;
      }

      if (!superApiEmbed.value.url) {
        setFailed(true);
        logError(
          LOGGER_MESSAGES.INIT_ERROR,
          { ...getDiagnosticMetadata(), trigger: 'setupEmbed', reason: 'missingUrlAfterFetch' },
          new Error('Embed URL is empty after fetch')
        );
        return;
      }

      embedIsLoading.value = true;
      createAndMountEmbed();
    }

    async function initializeEmbed() {
      if (hasFailed.value || isLoading.value) {
        return;
      }

      setLoading(true);

      if (needsSessionReset.value) {
        await store.dispatch('resetSuperStepSession');
      }

      try {
        await setupEmbed();
      } catch (e) {
        embedIsLoading.value = false;
        setFailed(true);
        logError(
          LOGGER_MESSAGES.INIT_ERROR,
          { ...getDiagnosticMetadata(), trigger: 'initializeEmbed' },
          e
        );
      } finally {
        setLoading(false);
      }
    }

    async function reloadWithSessionReset() {
      await store.dispatch('resetSuperStepSession');

      if (!hasFailed.value) {
        await initializeEmbed();
      }
    }

    async function reloadWithDataRefresh() {
      setLoading(true);
      setFailed(false);

      try {
        await store.dispatch('loadSuperApiData', sessionId.value);

        if (!hasFailed.value) {
          await setupEmbed();
        }
      } catch (e) {
        embedIsLoading.value = false;
        setFailed(true);
        logError(
          LOGGER_MESSAGES.INIT_ERROR,
          { ...getDiagnosticMetadata(), trigger: 'reloadWithDataRefresh' },
          e
        );
      } finally {
        setLoading(false);
      }
    }

    async function reload() {
      if (needsSessionReset.value) {
        await reloadWithSessionReset();
      } else {
        await reloadWithDataRefresh();
      }
    }

    async function moveToReviewStep() {
      try {
        await svc.onboarding.status(store.state.onboarding.id, 'REVIEW');
        store.commit('setOnboardingStatus', HR_ONBOARDING_STATES.REVIEW);
        await router.push({ name: 'view-module' });
      } catch (e) {
        logError(
          'Failed to navigate to review page from Tax and Super step',
          {},
          e
        );
      }
    }

    async function goToNextStep() {
      const nextDocumentIndex =
        store.getters.documents.findIndex(
          (doc) => doc.name === DOCUMENT_NAMES.SUPER_API
        ) + 1;

      // If this is the last document, navigate to review page
      if (nextDocumentIndex >= store.getters.documents.length) {
        await moveToReviewStep();
        return;
      }

      const nextDocument = store.getters.documents[nextDocumentIndex];
      store.commit('menuToggle', false);
      router.push({
        name: 'document-module',
        params: {
          onboardingId: store.state.onboarding.id,
          document: JSON.stringify(nextDocument),
          slug: nextDocument.slug,
        },
      });
    }

    onBeforeUnmount(() => {
      teardownEmbed();
      embedGeneration++;
    });

    onMounted(() => {
      initializeEmbed();

      track(EVENT_NAMES.ONBOARDING_TAX_AND_SUPER_OPENED);
    });

    return {
      t,
      isLoading,
      hasFailed,
      embedIsLoading,
      stepper,
      currentStepIndex,
      currentStepPercentage,
      reload,
      goToNextStep,
    };
  },
};
</script>

<style scoped>
.hr-super-api {
  background-color: var(--dpt-surface-color-expressive-blue-subtlest);
}
.hr-super-api-loading-failed {
  padding-top: var(--dpt-space-500);
  padding-bottom: var(--dpt-space-500);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--dpt-space-200);
}
.hr-super-api-loading-failed .__icon {
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #eff0f3;
  margin-bottom: var(--dpt-space-200);
}
.hr-super-api-loading-failed .__icon :deep(.co-icon) {
  width: 1.5rem;
  height: 1.5rem;
}
.hr-super-api-loading-failed .__title {
  font-size: 1rem;
  font-weight: 500;
  color: var(--co-text-primary);
  margin: 0;
}

.__stepper-progress-bar {
  position: absolute;
  inset-inline: 0;
  top: 0;

  width: 100%;
  height: 6px;
  background-color: var(--purple-400);
}
.__stepper-progress-bar-fill {
  height: 100%;
  background-color: var(--dpt-surface-color-accent);
  width: var(--percentage);
}
.__stepper-progress-info {
  font-size: 1rem;
  font-weight: 600;
  color: var(--dpt-text-color-secondary);

  padding-inline: var(--dpt-space-600);
  padding-top: var(--dpt-space-500);

  @media (min-width: 768px) {
    padding-top: var(--dpt-space-200);
  }
}
</style>

<style>
#super-api-embed iframe {
  border: none;
}
.hr-super-api-iframe-loader {
  display: none;
}
</style>
